import { useRef, useState } from "react"
import { AnimatePresence } from "motion/react"
import {
	IconClipboardContentOutline18,
	IconXmarkOutline18,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import type { ChatInputTextPaste } from "./chat-input-text-paste"
import { Badge } from "./badge"
import { textPasteLabel, textPasteMeta } from "./chat-input-text-paste"
import { IconButton } from "./icon-button"
import { Popover } from "./popover"

const PREVIEW_CLOSE_DELAY = 120

const TextPastePill = ({
	paste,
	onRemove,
}: {
	paste: ChatInputTextPaste
	onRemove: (id: string) => void
}) => {
	const [open, setOpen] = useState(false)
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

	const cancelClose = () => {
		if (closeTimer.current) clearTimeout(closeTimer.current)
		closeTimer.current = null
	}
	// The pointer crosses a gap on its way into the preview, so the close waits
	// long enough for it to land there and scroll.
	const scheduleClose = () => {
		cancelClose()
		closeTimer.current = setTimeout(() => setOpen(false), PREVIEW_CLOSE_DELAY)
	}
	const openNow = () => {
		cancelClose()
		setOpen(true)
	}

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
			modal={false}
			side="top"
			align="start"
			sideOffset={6}
			className="w-[320px] max-w-[min(320px,90vw)] p-0"
			content={
				<div
					onMouseEnter={openNow}
					onMouseLeave={scheduleClose}
					className="max-h-64 overflow-y-auto whitespace-pre-wrap px-3 py-2.5 pb-3 text-xs leading-relaxed text-secondary [overflow-wrap:anywhere]"
				>
					{paste.text}
				</div>
			}
		>
			<span
				onMouseEnter={openNow}
				onMouseLeave={scheduleClose}
				className="inline-flex"
			>
				<Badge
					variant="light-gray"
					Icon={IconClipboardContentOutline18}
					data-testid="chat-input-text-paste"
					className={cn(
						"h-6 max-w-[220px] cursor-default gap-1.5 pl-1.5 pr-0.5",
						open && "bg-gray-200 dark:bg-gray-700",
					)}
				>
					<span className="min-w-0 truncate">{textPasteLabel(paste.text)}</span>
					<span className="flex-none text-tertiary">
						{textPasteMeta(paste.text)}
					</span>
					<IconButton
						Icon={IconXmarkOutline18}
						iconStrokeWidth={1.5}
						variant="ghost"
						size="sm"
						aria-label="Remove pasted text"
						onClick={(e) => {
							e.stopPropagation()
							onRemove(paste.id)
						}}
						className="flex-none"
						iconClassName="h-3 w-3"
					/>
				</Badge>
			</span>
		</Popover>
	)
}

export const ChatInputTextPastes = ({
	pastes,
	onRemove,
	className,
}: {
	pastes: ChatInputTextPaste[]
	onRemove: (id: string) => void
	className?: string
}) => {
	if (pastes.length === 0) return null

	return (
		<div className={cn("flex flex-wrap gap-1.5", className)}>
			<AnimatePresence initial={false}>
				{pastes.map((paste) => (
					<TextPastePill key={paste.id} paste={paste} onRemove={onRemove} />
				))}
			</AnimatePresence>
		</div>
	)
}
